import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ApiClient } from '../api'
import type { Service, ServiceCreate, ServiceUpdate, EventServiceInfo } from '../types'

// Services query/mutation hooks. Same pattern as useComponents/useActivities:
// list query keyed on eventId; mutations write back into the cache so the
// UI reacts without a follow-up GET.

export function useServices(eventId: number) {
  return useQuery({
    queryKey: ['services', eventId],
    queryFn: () => ApiClient.services(eventId),
    enabled: Number.isFinite(eventId) && eventId > 0
  })
}

// Append the new service to the cached list. POST returns the canonical wire
// shape so this is a drop-in append; the ScheduleTab re-sorts by timeText on
// render, so a follow-up refetch isn't needed.
export function useCreateService(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: ServiceCreate) => ApiClient.createService(eventId, body),
    onSuccess: (created: Service) => {
      qc.setQueryData<Service[]>(['services', eventId], (old) =>
        old ? [...old, created] : [created]
      )
    }
  })
}

// Replace the matching service in the cached list.
export function useUpdateService(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: ServiceUpdate }) =>
      ApiClient.updateService(eventId, id, body),
    onSuccess: (updated: Service) => {
      qc.setQueryData<Service[]>(['services', eventId], (old) =>
        old ? old.map((s) => s.id === updated.id ? updated : s) : [updated]
      )
    }
  })
}

// Remove the service from the cached list. mutationFn returns the deleted id
// (API returns 204 with no body) so onSuccess knows what to filter.
export function useDeleteService(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      ApiClient.deleteService(eventId, id).then(() => id),
    onSuccess: (deletedId: number) => {
      qc.setQueryData<Service[]>(['services', eventId], (old) =>
        old ? old.filter((s) => s.id !== deletedId) : []
      )
    }
  })
}

// Dual-source timetable read for the Schedule tab. Picks manual
// dbo.Services rows OR MERAC-projected rows server-side depending on
// whether the event has a dbo.MeracEventMap binding. Separate cache
// key (['timetable', eventId]) so it doesn't race with the existing
// 'services' editing cache.
export function useEventTimetable(eventId: number) {
  return useQuery({
    queryKey: ['timetable', eventId],
    queryFn: () => ApiClient.eventTimetable(eventId),
    enabled: Number.isFinite(eventId) && eventId > 0,
    staleTime: 30_000
  })
}

// Presentation timetable for the printed docs (Product Briefing §4 +
// single-event Service Timetable PDF). Prefers manual Services, else the full
// per-day MERAC session projection — so MERAC-bound events aren't blank in the
// PDF the way reading dbo.Services directly left them.
export function usePresentationTimetable(eventId: number) {
  return useQuery({
    queryKey: ['presentation-timetable', eventId],
    queryFn: () => ApiClient.presentationTimetable(eventId),
    enabled: Number.isFinite(eventId) && eventId > 0,
    staleTime: 30_000
  })
}

// Per-event service header (Feature 2, mig 062). JOURNEY / RUN TIME / SET /
// CAPACITY block shown above the departures table and in the printed timetable.
export function useServiceInfo(eventId: number) {
  return useQuery({
    queryKey: ['service-info', eventId],
    queryFn: () => ApiClient.eventServiceInfo(eventId),
    enabled: Number.isFinite(eventId) && eventId > 0
  })
}

export function useUpdateServiceInfo(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: EventServiceInfo) => ApiClient.putEventServiceInfo(eventId, body),
    onSuccess: (saved: EventServiceInfo) => {
      qc.setQueryData(['service-info', eventId], saved)
    }
  })
}
